import { FlatList, StyleSheet, TouchableOpacity, View, ViewProps } from 'react-native';
import { Text, useTheme } from '@ui-kitten/components';
import React from 'react';
import moment from 'moment';

import { CHMedicationDto, CHAllergyDto, CHConditionDto, CHProcedureDto } from 'src/core/types/ConsolidateHealth';
import { UIHelper as uh } from '../../core';
import { HomeIcons } from '../home/HomeIcons';

//props
interface IHealthRecordListProps extends ViewProps {
  healthRecords: CHMedicationDto[] | CHAllergyDto[] | CHConditionDto[] | CHProcedureDto[];
  type: 'medications' | 'allergies' | 'conditions' | 'procedures';
  btnDetailHealthRecordClick: (type: string, item: any) => void;
}

const HealthRecordList = ({ healthRecords, type, btnDetailHealthRecordClick }: IHealthRecordListProps) => {
  const th = useTheme();

  const styleContainer = StyleSheet.create({
    container: {
      paddingLeft: uh.w2DP(16),
      paddingRight: uh.w2DP(16)
    },
    listItem: {
      justifyContent: 'space-between',
      borderColor: th['color-basic-600'],
      flexDirection: 'row',
      flex: 1,
      borderRadius: 8,
      marginTop: uh.h2DP(8),
      borderWidth: 1,
      paddingLeft: uh.w2DP(16),
      paddingRight: uh.w2DP(16),
      paddingTop: uh.h2DP(8),
      paddingBottom: uh.h2DP(10)
    },
    rightIcon: {
      width: 20,
      height: 20
    },
    dateContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: 110
    },
    statusBadge: {
      paddingHorizontal: uh.w2DP(8),
      paddingVertical: uh.h2DP(2),
      borderRadius: 12,
      marginRight: uh.w2DP(8)
    },
    statusText: {
      fontSize: 10,
      fontWeight: 'bold'
    }
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
        return th['color-success-500'];
      case 'inactive':
      case 'stopped':
        return th['color-warning-500'];
      case 'entered-in-error':
        return th['color-danger-500'];
      default:
        return th['color-basic-600'];
    }
  };

  const getDisplayDate = (record: any) => {
    switch (type) {
      case 'medications':
        return record.startDate || record.createdOn;
      case 'allergies':
        return record.onsetDate || record.recordedDate || record.createdOn;
      case 'conditions':
        return record.onsetDate || record.recordedDate || record.createdOn;
      case 'procedures':
        return record.performedDate || record.createdOn;
      default:
        return record.createdOn;
    }
  };

  const getAdditionalInfo = (record: any) => {
    switch (type) {
      case 'medications':
        return record.dosage ? `Dosage: ${JSON.stringify(record.dosage)}` : '';
      case 'allergies':
        return record.criticality ? `Criticality: ${record.criticality}` : '';
      case 'conditions':
        return record.category ? `Category: ${record.category}` : '';
      case 'procedures':
        return record.performedDate ? `Performed: ${moment(record.performedDate).format('DD MMM YYYY')}` : '';
      default:
        return '';
    }
  };

  const renderItem = ({ item }: { item: any }) => {
    const displayDate = getDisplayDate(item);
    const additionalInfo = getAdditionalInfo(item);

    return (
      <TouchableOpacity onPress={() => btnDetailHealthRecordClick(type, item)}>
        <View style={styleContainer.listItem}>
          <View style={{ flex: 1 }}>
            <Text category="s2">{item.name}</Text>
            {additionalInfo && (
              <Text category="c2" appearance="hint" style={{ marginTop: 2 }}>
                {additionalInfo}
              </Text>
            )}
            <View style={styleContainer.dateContainer}>
              <Text category="c1" appearance="hint" style={{ width: '70%' }}>
                {moment(displayDate).format('DD MMM YYYY')}
              </Text>
              <Text category="c1" appearance="hint" style={{ width: '100%' }}>
                {moment(displayDate).format('HH:mm')}
              </Text>
            </View>
          </View>
          <View style={{ justifyContent: 'center', alignItems: 'flex-end' }}>
            <View style={[styleContainer.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
              <Text style={[styleContainer.statusText, { color: 'white' }]}>{item.status}</Text>
            </View>
            <HomeIcons.ForwardIcon style={styleContainer.rightIcon} fill={th['color-basic-600']} />
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (healthRecords.length === 0) {
    return (
      <View style={styleContainer.container}>
        <Text category="s1" appearance="hint" style={{ textAlign: 'center', marginTop: uh.h2DP(20) }}>
          No {type} records found
        </Text>
      </View>
    );
  }

  return (
    <View style={styleContainer.container}>
      <FlatList
        data={healthRecords}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        scrollEnabled={false}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

export default HealthRecordList;
